import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
} from 'recharts';

export const PurchaseFrequencyChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance } = useBoardChartData();

    const chartData = useMemo(() => {
        if (!finance?.transactions) return [];

        const ordersByCustomer: Record<string, number> = {};

        for (const tx of finance.transactions) {
            if (['product_sale', 'sale'].includes(tx.category as string)) {
                const actor = tx.actor as Record<string, unknown> | null;
                const customerId = actor?.id as string;
                if (customerId) {
                    ordersByCustomer[customerId] = (ordersByCustomer[customerId] || 0) + 1;
                }
            }
        }

        const buckets: Record<string, number> = {
            '1': 0,
            '2': 0,
            '3': 0,
            '4+': 0,
        };

        for (const count of Object.values(ordersByCustomer)) {
            if (count === 1) buckets['1']++;
            else if (count === 2) buckets['2']++;
            else if (count === 3) buckets['3']++;
            else buckets['4+']++;
        }

        return [
            { bucket: t('charts.orderCount', '{{n}} order', { n: 1 }), customers: buckets['1'] },
            { bucket: t('charts.orderCountPlural', '{{n}} orders', { n: 2 }), customers: buckets['2'] },
            { bucket: t('charts.orderCountPlural', '{{n}} orders', { n: 3 }), customers: buckets['3'] },
            { bucket: t('charts.orderCountPluralPlus', '4+ orders'), customers: buckets['4+'] },
        ];
    }, [finance?.transactions, t]);

    const isEmpty = useMemo(
        () => chartData.length === 0 || chartData.every((d) => d.customers === 0),
        [chartData]
    );

    useSetChartMetadata({ isEmpty });

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        data={chartData}
                        margin={CHART_STYLES.margin.withAxis}
                    >
                        <CartesianGrid
                            vertical={false}
                            stroke={CHART_STYLES.grid.stroke}
                            strokeDasharray={CHART_STYLES.grid.strokeDasharray}
                            strokeWidth={CHART_STYLES.grid.strokeWidth}
                            opacity={CHART_STYLES.grid.opacity}
                        />
                        <XAxis
                            dataKey="bucket"
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            tickMargin={CHART_STYLES.axis.tickMargin}
                        />
                        <YAxis
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            allowDecimals={false}
                        />
                        <Tooltip
                            contentStyle={CHART_STYLES.tooltip.contentStyle}
                            cursor={CHART_STYLES.tooltip.cursor}
                            formatter={(value: number) => [
                                value,
                                t('charts.customers', 'Customers'),
                            ]}
                        />
                        <Bar
                            dataKey="customers"
                            name={t('charts.customers', 'Customers')}
                            fill={CHART_COLORS.primary}
                            radius={CHART_STYLES.bar.radius}
                            maxBarSize={CHART_STYLES.bar.maxBarSize}
                        />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
